// L'EFFACEUR — Onglet client "Mes tickets support"

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { MessageSquare, Plus, Send, RefreshCw, X } from 'lucide-react';
import { API } from '../../../App';
import { Button } from '../../ui/button';
import EmptyState from '../../ui/empty-state';
import { SkeletonCard } from '../../ui/loading-skeleton';

const STATUS_LABELS = {
  open: { label: 'Ouvert', color: 'text-cyan-400' },
  in_progress: { label: 'En cours de traitement', color: 'text-orange-400' },
  waiting_customer: { label: 'Réponse de l\'équipe — à vous de réagir', color: 'text-magenta-400' },
  resolved: { label: 'Résolu', color: 'text-success' },
  closed: { label: 'Fermé', color: 'text-gray-500' },
};

const SupportTab = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newTicket, setNewTicket] = useState({ subject: '', message: '', category: 'general', priority: 'normal' });
  const [reply, setReply] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/support/tickets`, { headers: { Authorization: `Bearer ${token}` } });
      setTickets(res.data.tickets || []);
    } catch (e) {
      toast.error('Erreur chargement tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const createTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error('Sujet et message requis');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/support/tickets`, newTicket, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Ticket créé. Réponse sous 24h ouvrées.');
      setCreating(false);
      setNewTicket({ subject: '', message: '', category: 'general', priority: 'normal' });
      fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur création ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/support/tickets/${selected.ticket_id}/reply`,
        { message: reply },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Message envoyé');
      setReply('');
      // Refresh
      const res = await axios.get(`${API}/support/tickets/${selected.ticket_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelected(res.data);
      fetchTickets();
    } catch (e) {
      toast.error('Erreur envoi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-orbitron text-2xl font-bold text-white mb-1">Support & Aide</h2>
          <p className="text-gray-400 text-sm">Réponse sous 24h ouvrées</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchTickets} variant="outline" className="text-xs">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <Button onClick={() => setCreating(true)} className="bg-cyan-400 text-black hover:bg-cyan-300 font-bold">
            <Plus className="w-4 h-4 mr-1" /> Nouveau ticket
          </Button>
        </div>
      </div>

      {loading ? (
        <SkeletonCard />
      ) : tickets.length === 0 ? (
        <EmptyState
          icon={MessageSquare}
          title="Aucun ticket"
          description="Une question ? Un blocage ? Crée un ticket et notre équipe répond sous 24h ouvrées."
          action={
            <Button onClick={() => setCreating(true)} className="bg-cyan-400 text-black hover:bg-cyan-300">
              <Plus className="w-4 h-4 mr-1" /> Créer un ticket
            </Button>
          }
        />
      ) : (
        <div className="space-y-2">
          {tickets.map(t => (
            <div
              key={t.ticket_id}
              onClick={() => setSelected(t)}
              className="glass-card border border-gray-800 hover:border-cyan-400/40 p-4 cursor-pointer transition-colors"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold mb-1">{t.subject}</div>
                  <div className={`text-xs font-mono ${STATUS_LABELS[t.status]?.color}`}>{STATUS_LABELS[t.status]?.label}</div>
                  <div className="text-xs text-gray-500 mt-2">{t.messages?.length || 0} message(s) · créé le {t.created_at?.slice(0, 10)}</div>
                </div>
                {t.status === 'waiting_customer' && (
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-magenta-400/20 text-magenta-400 border border-magenta-400/40">
                    À VOUS DE RÉPONDRE
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal création */}
      {creating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setCreating(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="glass-card border border-cyan-400/40 p-6 max-w-xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-orbitron text-lg font-bold text-white">Nouveau ticket support</h3>
              <button onClick={() => setCreating(false)} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-mono text-gray-400 uppercase mb-1">Sujet</label>
                <input
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Résumé en quelques mots"
                  maxLength={200}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-cyan-400 focus:outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-mono text-gray-400 uppercase mb-1">Catégorie</label>
                  <select value={newTicket.category} onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm">
                    <option value="general">Général</option>
                    <option value="mission">Question sur une mission</option>
                    <option value="billing">Facturation / paiement</option>
                    <option value="technical">Problème technique</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-mono text-gray-400 uppercase mb-1">Priorité</label>
                  <select value={newTicket.priority} onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm">
                    <option value="low">Basse (48h)</option>
                    <option value="normal">Normale (24h)</option>
                    <option value="high">Haute (8h)</option>
                    <option value="urgent">Urgente (4h)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-mono text-gray-400 uppercase mb-1">Message</label>
                <textarea value={newTicket.message} onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                  placeholder="Décrivez votre problème ou votre question en détail..."
                  rows={6} maxLength={5000}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-cyan-400 focus:outline-none" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button onClick={() => setCreating(false)} variant="outline">Annuler</Button>
                <Button onClick={createTicket} disabled={submitting} className="bg-cyan-400 text-black hover:bg-cyan-300 font-bold">
                  {submitting ? 'Création...' : 'Créer le ticket'}
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal détail ticket */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setSelected(null)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="glass-card border border-cyan-400/40 p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-orbitron text-lg font-bold text-white">{selected.subject}</h3>
                <div className={`text-xs font-mono ${STATUS_LABELS[selected.status]?.color} mt-1`}>{STATUS_LABELS[selected.status]?.label}</div>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3 mb-4 max-h-80 overflow-y-auto">
              {(selected.messages || []).map((m, idx) => (
                <div key={idx} className={`p-3 rounded ${m.from === 'admin' ? 'bg-cyan-500/10 border border-cyan-500/30' : 'bg-gray-900/50 border border-gray-800'}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-mono ${m.from === 'admin' ? 'text-cyan-400' : 'text-gray-400'}`}>
                      {m.from === 'admin' ? "L'équipe L'EFFACEUR" : 'Vous'}
                    </span>
                    <span className="text-[10px] text-gray-600">{new Date(m.created_at).toLocaleString('fr-FR')}</span>
                  </div>
                  <div className="text-sm text-gray-200 whitespace-pre-wrap">{m.body}</div>
                </div>
              ))}
            </div>

            {selected.status !== 'closed' && (
              <div className="border-t border-gray-800 pt-3">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Votre message..."
                  rows={3} maxLength={5000}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded text-white text-sm focus:border-cyan-400 focus:outline-none" />
                <div className="flex justify-end mt-2">
                  <Button onClick={sendReply} disabled={submitting || !reply.trim()} className="bg-cyan-400 text-black hover:bg-cyan-300 font-bold">
                    <Send className="w-3 h-3 mr-1" /> {submitting ? 'Envoi...' : 'Envoyer'}
                  </Button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SupportTab;
